import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { project, rubberband, Spring } from './spring'

export type PagerPhoto = { src: string; alt: string }
export type PhotoPagerHandle = { go: (direction: 1 | -1) => void }

type Props = {
  photos: PagerPhoto[]
  index: number
  onIndexChange: (index: number) => void
  zoom: number
  onZoomChange: (zoom: number) => void
  minZoom?: number
  maxZoom?: number
  className?: string
}

const GAP = 24
const PAGE = { response: 0.38, damping: 1 }
const SETTLE = { response: 0.4, damping: 1 }
const wrap = (value: number, count: number) => ((value % count) + count) % count
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

type Gesture =
  | { mode: 'pending' | 'swipe' | 'pan'; startX: number; startY: number; startOffset: number; startPanX: number; startPanY: number; time: number }
  | { mode: 'pinch'; startDistance: number; startMidX: number; startMidY: number; startScale: number; startPanX: number; startPanY: number }

// Photos follow the finger 1:1, flicks carry their velocity into a spring, and
// any motion can be grabbed again mid-flight. Neighbours stay mounted on either
// side so the next photo slides in instead of popping.
export const PhotoPager = forwardRef<PhotoPagerHandle, Props>(function PhotoPager({ photos, index, onIndexChange, zoom, onZoomChange, minZoom = 1, maxZoom = 3, className = '' }, ref) {
  const count = photos.length
  const root = useRef<HTMLDivElement>(null)
  const track = useRef<HTMLDivElement>(null)
  const current = useRef<HTMLDivElement>(null)
  const size = useRef({ width: 0, height: 0 })
  const render = useRef(() => {})
  const [springs] = useState(() => {
    const draw = () => render.current()
    return { offset: new Spring(0, draw), scale: new Spring(1, draw, 0.001), panX: new Spring(0, draw), panY: new Spring(0, draw) }
  })
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const history = useRef<Array<{ t: number; x: number; y: number }>>([])
  const gesture = useRef<Gesture | null>(null)
  const lastTap = useRef<{ t: number; x: number; y: number } | null>(null)
  const directionHint = useRef<1 | -1 | null>(null)
  const latest = useRef({ index, count, onIndexChange, onZoomChange, minZoom, maxZoom })
  latest.current = { index, count, onIndexChange, onZoomChange, minZoom, maxZoom }

  // Slide keys follow an unbounded position so the incoming photo keeps its
  // element (and decoded image) when it becomes the current one.
  const [position, setPosition] = useState(0)
  const [previousIndex, setPreviousIndex] = useState(index)
  if (index !== previousIndex) {
    const forward = wrap(index - previousIndex, count)
    const direction = directionHint.current ?? (forward <= count / 2 ? forward : forward - count)
    setPreviousIndex(index)
    setPosition(value => value + direction)
  }

  render.current = () => {
    if (track.current) track.current.style.transform = `translate3d(${springs.offset.value}px, 0, 0)`
    if (current.current) current.current.style.transform = `translate3d(${springs.panX.value}px, ${springs.panY.value}px, 0) scale(${springs.scale.value})`
  }

  const stride = () => size.current.width + GAP

  // Pan limits keep the zoomed photo's edges from leaving the frame.
  const panBounds = (scale: number) => {
    const image = current.current?.querySelector('img')
    if (!image) return { x: 0, y: 0 }
    return {
      x: Math.max(0, (image.offsetWidth * scale - size.current.width) / 2),
      y: Math.max(0, (image.offsetHeight * scale - size.current.height) / 2),
    }
  }

  const settlePan = (scale: number, velocityX = 0, velocityY = 0) => {
    const bounds = panBounds(scale)
    const clamp = (value: number, bound: number) => Math.min(bound, Math.max(-bound, value))
    springs.panX.to(clamp(springs.panX.value + project(velocityX), bounds.x), { ...SETTLE, velocity: velocityX })
    springs.panY.to(clamp(springs.panY.value + project(velocityY), bounds.y), { ...SETTLE, velocity: velocityY })
  }

  const zoomTo = (scale: number, focusX = 0, focusY = 0) => {
    const { minZoom, maxZoom, onZoomChange } = latest.current
    const target = Math.min(maxZoom, Math.max(minZoom, scale))
    const ratio = target / springs.scale.value
    // Keep the point under the finger (relative to the centre) in place.
    const bounds = panBounds(target)
    const clamp = (value: number, bound: number) => Math.min(bound, Math.max(-bound, value))
    springs.scale.to(target, SETTLE)
    springs.panX.to(clamp(focusX - (focusX - springs.panX.value) * ratio, bounds.x), SETTLE)
    springs.panY.to(clamp(focusY - (focusY - springs.panY.value) * ratio, bounds.y), SETTLE)
    if (Math.abs(target - zoomRef.current) > 0.001) {
      zoomRef.current = target
      onZoomChange(target)
    }
  }

  const go = (direction: 1 | -1, velocity = 0) => {
    const { index, count, onIndexChange } = latest.current
    if (count < 2) {
      springs.offset.to(0, { ...SETTLE, velocity })
      return
    }
    directionHint.current = direction
    pendingVelocity.current = velocity
    onIndexChange(wrap(index + direction, count))
  }
  const pendingVelocity = useRef(0)
  useImperativeHandle(ref, () => ({ go: (direction) => go(direction) }))

  // Rebase onto the new current slide without a visible jump, then settle.
  const previousPosition = useRef(position)
  useLayoutEffect(() => {
    const direction = position - previousPosition.current
    previousPosition.current = position
    directionHint.current = null
    if (!direction) return
    springs.scale.set(1)
    springs.panX.set(0)
    springs.panY.set(0)
    zoomRef.current = 1
    if (prefersReducedMotion()) {
      springs.offset.set(0)
      current.current?.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' })
      return
    }
    const velocity = pendingVelocity.current
    pendingVelocity.current = 0
    springs.offset.value += direction * stride()
    springs.offset.to(0, { ...PAGE, velocity })
    render.current()
  }, [position, springs])

  // Zoom buttons and keyboard shortcuts drive the same springs as pinching.
  const zoomRef = useRef(zoom)
  useEffect(() => {
    if (Math.abs(zoom - zoomRef.current) < 0.001) return
    zoomRef.current = zoom
    const ratio = zoom / springs.scale.value
    const bounds = panBounds(zoom)
    const clamp = (value: number, bound: number) => Math.min(bound, Math.max(-bound, value))
    if (prefersReducedMotion()) {
      springs.scale.set(zoom)
      springs.panX.set(clamp(springs.panX.value * ratio, bounds.x))
      springs.panY.set(clamp(springs.panY.value * ratio, bounds.y))
      return
    }
    springs.scale.to(zoom, SETTLE)
    springs.panX.to(clamp(springs.panX.value * ratio, bounds.x), SETTLE)
    springs.panY.to(clamp(springs.panY.value * ratio, bounds.y), SETTLE)
  }, [zoom, springs])

  useLayoutEffect(() => {
    const element = root.current
    if (!element) return
    const measure = () => { size.current = { width: element.clientWidth, height: element.clientHeight } }
    measure()
    const observer = new window.ResizeObserver(measure)
    observer.observe(element)
    return () => {
      observer.disconnect()
      Object.values(springs).forEach(spring => spring.stop())
    }
  }, [springs])

  const relative = (x: number, y: number) => {
    const bounds = root.current!.getBoundingClientRect()
    return { x: x - bounds.left - bounds.width / 2, y: y - bounds.top - bounds.height / 2 }
  }

  const midpoint = () => {
    const points = [...pointers.current.values()]
    const x = points.reduce((sum, point) => sum + point.x, 0) / points.length
    const y = points.reduce((sum, point) => sum + point.y, 0) / points.length
    return { x, y }
  }

  const startSingle = (x: number, y: number) => {
    gesture.current = { mode: 'pending', startX: x, startY: y, startOffset: springs.offset.value, startPanX: springs.panX.value, startPanY: springs.panY.value, time: performance.now() }
    history.current = [{ t: performance.now(), x, y }]
  }

  const startPinch = () => {
    const [a, b] = [...pointers.current.values()]
    const mid = relative((a.x + b.x) / 2, (a.y + b.y) / 2)
    gesture.current = { mode: 'pinch', startDistance: Math.hypot(a.x - b.x, a.y - b.y) || 1, startMidX: mid.x, startMidY: mid.y, startScale: springs.scale.value, startPanX: springs.panX.value, startPanY: springs.panY.value }
  }

  const velocity = () => {
    const samples = history.current
    const now = performance.now()
    const recent = samples.filter(sample => now - sample.t < 100)
    if (recent.length < 2) return { x: 0, y: 0 }
    const first = recent[0]
    const last = recent[recent.length - 1]
    const elapsed = Math.max(1, last.t - first.t) / 1000
    return { x: (last.x - first.x) / elapsed, y: (last.y - first.y) / elapsed }
  }

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    // Grab whatever is moving from its on-screen value.
    Object.values(springs).forEach(spring => spring.stop())
    if (pointers.current.size === 1) startSingle(event.clientX, event.clientY)
    else if (pointers.current.size === 2) startPinch()
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId)) return
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    const active = gesture.current
    if (!active) return
    const now = performance.now()
    if (active.mode === 'pinch') {
      if (pointers.current.size < 2) return
      const [a, b] = [...pointers.current.values()]
      const { minZoom, maxZoom } = latest.current
      let scale = active.startScale * Math.hypot(a.x - b.x, a.y - b.y) / active.startDistance
      if (scale > maxZoom) scale = maxZoom + rubberband(scale - maxZoom, maxZoom, 0.4)
      if (scale < minZoom) scale = minZoom - rubberband(minZoom - scale, minZoom, 0.4)
      const mid = relative((a.x + b.x) / 2, (a.y + b.y) / 2)
      const ratio = scale / active.startScale
      springs.scale.value = scale
      springs.panX.value = mid.x - (active.startMidX - active.startPanX) * ratio
      springs.panY.value = mid.y - (active.startMidY - active.startPanY) * ratio
      const center = midpoint()
      history.current.push({ t: now, x: center.x, y: center.y })
      render.current()
      return
    }
    const dx = event.clientX - active.startX
    const dy = event.clientY - active.startY
    history.current.push({ t: now, x: event.clientX, y: event.clientY })
    if (history.current.length > 12) history.current.shift()
    if (active.mode === 'pending') {
      const threshold = event.pointerType === 'mouse' ? 4 : 10
      if (Math.hypot(dx, dy) < threshold) return
      if (springs.scale.value > 1.01) active.mode = 'pan'
      else if (Math.abs(dx) > Math.abs(dy)) active.mode = 'swipe'
      else { gesture.current = null; return }
    }
    if (active.mode === 'swipe') {
      const width = size.current.width
      springs.offset.value = latest.current.count > 1 ? active.startOffset + dx : rubberband(active.startOffset + dx, width)
    } else {
      const bounds = panBounds(springs.scale.value)
      const resist = (value: number, bound: number, dimension: number) =>
        value > bound ? bound + rubberband(value - bound, dimension) : value < -bound ? -bound + rubberband(value + bound, dimension) : value
      springs.panX.value = resist(active.startPanX + dx, bounds.x, size.current.width)
      springs.panY.value = resist(active.startPanY + dy, bounds.y, size.current.height)
    }
    render.current()
  }

  const onPointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId)) return
    pointers.current.delete(event.pointerId)
    const active = gesture.current
    if (active?.mode === 'pinch') {
      if (pointers.current.size === 1) {
        // Lifting one finger hands over to panning with the other.
        const [point] = [...pointers.current.values()]
        startSingle(point.x, point.y)
        if (gesture.current && gesture.current.mode !== 'pinch') gesture.current.mode = 'pan'
        const scale = springs.scale.value
        const { minZoom, maxZoom } = latest.current
        if (scale < minZoom || scale > maxZoom) zoomTo(scale)
        return
      }
      if (pointers.current.size === 0) {
        gesture.current = null
        zoomTo(springs.scale.value)
      }
      return
    }
    if (pointers.current.size > 0) return
    gesture.current = null
    if (!active) return
    const speed = velocity()
    if (active.mode === 'swipe') {
      const projected = springs.offset.value + project(speed.x)
      const threshold = stride() / 2
      if (projected < -threshold && latest.current.count > 1) go(1, speed.x)
      else if (projected > threshold && latest.current.count > 1) go(-1, speed.x)
      else springs.offset.to(0, { ...SETTLE, velocity: speed.x })
      return
    }
    if (active.mode === 'pan') {
      settlePan(springs.scale.value, speed.x, speed.y)
      return
    }
    if (event.type === 'pointercancel') return
    // A tap that didn't move: two in quick succession toggle zoom at that point.
    const now = performance.now()
    const tap = { t: now, x: event.clientX, y: event.clientY }
    const previous = lastTap.current
    if (previous && now - previous.t < 320 && Math.hypot(tap.x - previous.x, tap.y - previous.y) < 30) {
      lastTap.current = null
      const focus = relative(tap.x, tap.y)
      const { minZoom, maxZoom } = latest.current
      if (springs.scale.value > minZoom + 0.01) zoomTo(minZoom)
      else zoomTo(Math.min(maxZoom, Math.max(2, minZoom * 2)), focus.x, focus.y)
    } else {
      lastTap.current = tap
    }
  }

  const slots = count > 1 ? [-1, 0, 1] : [0]
  return <div ref={root} className={`photo-pager ${className}`.trim()} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerEnd} onPointerCancel={onPointerEnd}
    onDragStart={(event) => event.preventDefault()}>
    <div ref={track} className="photo-pager-track">
      {slots.map(slot => {
        const photo = photos[wrap(index + slot, count)]
        if (!photo) return null
        return <div className="photo-pager-slide" key={position + slot} style={{ transform: `translateX(calc(${slot} * (100% + ${GAP}px)))` }} aria-hidden={slot !== 0 || undefined}>
          <div className="photo-pager-zoom" ref={slot === 0 ? current : undefined} style={slot === 0 ? undefined : { transform: 'none' }}>
            <img src={photo.src} alt={slot === 0 ? photo.alt : ''} draggable={false} decoding="async" />
          </div>
        </div>
      })}
    </div>
  </div>
})
